/**
 * A WordPress-style filter/action hook engine.
 *
 * This is the extension backbone of the plugin: themes register callbacks via addFilter()/addAction()
 * (exposed publicly on the PostFilters instance) and core code fires them via applyFilter()/doAction().
 *
 * Callbacks are invoked with `this` bound to the PostFilters instance (set via setContext()) — this is a
 * long-standing public contract that existing theme integrations depend on, so it must be preserved.
 */
export default class Hooks {
    /** @member {object} */
    #hooks = {filters: {}, actions: {}};

    /** @member {Logger} */
    #logger;

    /** @member {*} The `this` context callbacks are bound to (the PostFilters instance). */
    #context = null;

    /**
     * @param {Logger} logger
     */
    constructor(logger) {
        this.#logger = logger;
    }

    /**
     * Sets the context (`this`) that hook callbacks are invoked with. Preserves the historical behavior
     * where callbacks receive the PostFilters instance as `this`.
     *
     * @param {*} context
     * @returns {Hooks}
     */
    setContext(context) {
        this.#context = context;
        return this;
    }

    /**
     * Registers a filter callback. Mirrors WordPress add_filter(): lower priority runs first, and each
     * callback receives (and should return) the running value.
     *
     * @param {string} name
     * @param {Function} callback
     * @param {int} priority
     */
    addFilter(name, callback, priority = 10) {
        this.#addHook("filters", name, callback, priority);
    }

    /**
     * Registers an action callback. Mirrors WordPress add_action(): lower priority runs first.
     *
     * @param {string} name
     * @param {Function} callback
     * @param {int} priority
     */
    addAction(name, callback, priority = 10) {
        this.#addHook("actions", name, callback, priority);
    }

    /**
     * Runs a filter chain and returns the (possibly modified) value.
     *
     * @param {string} name
     * @param {*} val
     * @param {*} args
     * @returns {*}
     */
    applyFilter(name, val, ...args) {
        return this.#runHook("filters", name, val, ...args);
    }

    /**
     * Fires an action.
     *
     * @param {string} name
     * @param {*} args
     */
    doAction(name, ...args) {
        this.#runHook("actions", name, null, ...args);
    }

    /**
     * The raw registered hooks, exposed for debugging (see PostFilters.printHooks()).
     * @returns {object}
     */
    get hooks() {
        return this.#hooks;
    }

    /**
     * Stores a callback under a hook type/name/priority.
     *
     * @param {string} type
     * @param {string} name
     * @param {Function} callback
     * @param {int} priority
     */
    #addHook(type, name, callback, priority = 10) {
        this.#hooks[type][name] = this.#hooks[type][name] || {};
        this.#hooks[type][name][priority] = this.#hooks[type][name][priority] || [];
        this.#hooks[type][name][priority].push(callback);
    }

    /**
     * Loops through all callbacks hooked into a given hook and runs them in priority order. Filters chain
     * the return value; actions ignore it. Every callback is wrapped in try/catch and bound to the context.
     *
     * @param {string} type "actions" or "filters"
     * @param {string} name
     * @param {*} val
     * @param {*} args
     * @returns {*}
     */
    #runHook(type, name, val, ...args) {
        const hooks = this.#hooks[type][name] || false;
        let ret = val;

        if(hooks && Object.keys(hooks).length > 0) {
            const label = (type === "actions") ? "Action" : "Filter";

            this.#logger.timerStart("Run Time");
            let counter = 0;
            let errors = 0;

            this.#logger
                .start(`${label}: ${name}`)
                .groupStart("Arguments", "", this.#logger.COLOR.PURPLE)
                .table(args, ["Value"])
                .groupEnd()
                .groupStart(`${label}s`, "", this.#logger.COLOR.PURPLE);

            for(let priority in hooks) {
                this.#logger.groupStart("Priority", priority, this.#logger.COLOR.GREEN);

                for(let i in hooks[priority]) {
                    counter++;
                    this.#logger
                        .groupStart(label, "", this.#logger.COLOR.TURQUOISE)
                        .timerStart(`${label} Run Time`)
                        .groupStart("Console", "", this.#logger.COLOR.CYAN);

                    try {
                        if(type === "actions") {
                            hooks[priority][i].apply(this.#context, args);
                        } else {
                            ret = hooks[priority][i].call(this.#context, ret, ...args);
                        }
                    } catch(e) {
                        errors++;
                        this.#logger.error(e);
                    }

                    this.#logger
                        .groupEnd()
                        .timerEnd(`${label} Run Time`)
                        .dir(hooks[priority][i])
                        .groupEnd();
                }

                this.#logger.groupEnd();
            }

            this.#logger
                .groupEnd()
                .groupStart("Stats", "", this.#logger.COLOR.PURPLE)
                .log(`${label} Count:`, counter)
                .log("Error Count:", errors);

            if(type === 'filters') {
                this.#logger.log("Final Value:", ret);
            }

            this.#logger
                .timerEnd("Run Time")
                .groupEnd()
                .end();
        } else {
            this.#hooks[type][name] = {};
        }

        return ret;
    }
}
